import React from "react";
import { Box, Typography, Button, Grid, Card, CardMedia, useMediaQuery, useTheme } from "@mui/material";
import { AboutUsImage } from "../../Data/image";
import { AboutUsData } from "../../Data/data";
import { buttonColor, buttonHoverColor, fontFamilyall } from "../../UtilColorPalate/UtilColorPalate";
import { Link } from "react-router-dom";
import { Verified, EmojiObjects, People, TrendingUp } from "@mui/icons-material";

const icons = [Verified, EmojiObjects, People, TrendingUp];

const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Detects screens smaller than 'md' (900px)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Detects screens smaller than 'sm' (600px)

  return (
    <Box sx={{ paddingTop: isMobile ? "20%" : "7%", paddingX: isSmallScreen ? 2 : 4 }}>
      {/* Hero Section */}
      <Box
        sx={{
          paddingY: isMobile ? "30px" : "40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid container spacing={isMobile ? 3 : 6} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6} sx={{ animation: "fadeIn 1s ease-in-out"}}>
            <Typography
              variant={isSmallScreen ? "h4" : "h3"}
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#003366", fontFamily: fontFamilyall, padding:"10px" }}
            >
              {AboutUsData.aboutTitle}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                textAlign: "justify",
                lineHeight: 1.8,
                fontSize: isSmallScreen ? "0.95rem" : "1.1rem",
                fontFamily: fontFamilyall,
                padding:"10px" 
              }}
            >
              {AboutUsData.aboutDescription}
            </Typography>
          </Grid>

          {/* Right Image */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
                borderRadius: "16px",
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(10px)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": { transform: isMobile ? "none" : "scale(1.05)" }, // Disable hover effect on mobile
              }}
            >
              <CardMedia
                component="img"
                image={AboutUsImage}
                alt="Team Discussion"
                sx={{
                  width: "100%",
                  height: isMobile ? "250px" : "350px", // Smaller height on mobile
                  objectFit: "cover",
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Core Values Section */}
      <Box sx={{ textAlign: "center", my: isMobile ? 4 : 6 }}>
        <Typography
          variant={isSmallScreen ? "h4" : "h3"}
          fontWeight="bold"
          gutterBottom
          sx={{ color: "#003366", fontFamily: fontFamilyall }}
        >
          {AboutUsData.coreValuesTitle}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          paragraph
          sx={{
            maxWidth: isMobile ? "100%" : "800px",
            margin: "auto",
            textAlign: "justify",
            lineHeight: 1.8,
            fontSize: isSmallScreen ? "0.95rem" : "1.1rem",
            fontFamily: fontFamilyall,
          }}
        >
          {AboutUsData.coreValuesDescription}
        </Typography>
      </Box>

      {/* Core Values Grid with Icons */}
      <Grid container spacing={isMobile ? 2 : 4} sx={{ maxWidth: "1000px", margin: "auto" }}>
        {AboutUsData.coreValues.map((value, index) => {
          const IconComponent = icons[index % icons.length];
          return (
            <Grid
              item
              xs={12}
              sm={6} // Two columns on small screens and above
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: 2,
                transition: "transform 0.3s ease-in-out",
                "&:hover": { transform: isMobile ? "none" : "scale(1.05)" }, // Disable hover on mobile
              }}
            >
              <IconComponent sx={{ fontSize: isSmallScreen ? "2.5rem" : "3rem", color: "#003366" }} />
              <Box>
                <Typography
                  variant={isSmallScreen ? "h6" : "h5"}
                  fontWeight="bold"
                  sx={{ color: "#003366", fontFamily: fontFamilyall }}
                >
                  {value.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontSize: isSmallScreen ? "0.9rem" : "1.05rem",
                    fontFamily: fontFamilyall,
                  }}
                >
                  {value.description}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Contact Us Button */}
      <Box sx={{ textAlign: "center", mt: isMobile ? 4 : 6, mb: 6 }}>
        <Link to={AboutUsData?.contactUsLink} style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            sx={{
                               backgroundColor: buttonColor,
                               color: "#fff",
                               textTransform: "none",
                               padding: "5px 15px",
                               borderRadius: "4px",
                               "&:hover": { backgroundColor: buttonHoverColor },
                             }}
          >
            {AboutUsData.contactUsText}
          </Button>
        </Link>
      </Box>

      {/* Keyframes Animation */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default AboutUs;